import React, {
  FormEvent, useEffect, useState,
} from "react";
import { AlertContextProps, } from "contexts/AlertContext";
import { useAlert, useSettings, } from "hooks";
import CreateView from "components/Views/CreateView";
import { axios, } from "libs";
import { Validation, } from "interfaces/responses";
import { useNavigate, useParams, } from "react-router-dom";
import { Grid, Select, TextInput, } from "@mantine/core";
import { resolveTheme, } from "themes/main";
import { Role, } from "../../interfaces/main";
import { UserForm, } from "./users";

function CreateUser(): JSX.Element {
  const alertContext : AlertContextProps = useAlert();

  const [dropdowns, setDropdowns] = useState<{
    roles: Array<Role>;
  }>({
    roles: [],
  });

  const [formData, setFormData] = useState<UserForm>({
    name: "",
    email: "",
    role_id: 0,
  });

  const { organization_id, } = useParams();

  const [validated, setValidated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [validationRules, setValidationRules] = useState<Validation>();
  const [requiredFields, setRequiredFields] = useState<string[]>([]);

  const navigate = useNavigate();
  const onSubmit = (e: FormEvent): void => {
    const form = e.currentTarget as HTMLFormElement;
    if (!form.checkValidity()) {
      setValidated(true);
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();

    setLoading(true);
    axios.post(`/organizations/${organization_id}/users`, formData).then((res) => {
      const newUser = res.data.data;
      alertContext.setAlert({
        type: "success",
        heading: "Success",
        message: "User created successfully",
      });
      navigate(`/organizations/${organization_id}/users/${newUser.id}`);
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error(err);
      alertContext.setAlert({
        type: "danger",
        heading: "Error",
        message: "Error creating user",
      });
      setLoading(false);
    }).finally(() => {
      setLoading(false);
    });
  };

  const onError: Function = (): void => {
    alertContext.setAlert({
      type: "danger",
      heading: "Error",
      message: "Error creating user",
    });
  };

  useEffect(() => {
    setLoading(true);
    axios.get(`/organizations/${organization_id}/users/create`).then((res) => {
      const { validation, } = res.data.data as { validation: Validation };
      setRequiredFields(
        Object.keys(validation.rules).filter((rule) => {
          if (!validation.rules[rule].includes("required")) {
            return false;
          }
          return true;
        })
      );
      setValidationRules(validation);
    }).catch((err) => {
      onError(err);
    });

    setLoading(true);
    axios.get("/roles").then((res) => {
      setDropdowns({
        ...dropdowns,
        roles: res.data.data,
      });
    }).catch((err) => {
      onError(err);
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  const { theme: { current, }, } = useSettings();
  const currentTheme = resolveTheme(current);
  const { colors, } = currentTheme;

  return (
    <CreateView
      title="Create User"
      loading={loading}
      formState={formData}
      requiredFields={requiredFields}
      setValidated={setValidated}
      buttons={[
        {
          text: "Submit",
          handleClick: onSubmit,
          color: colors.primary,
          variant: "filled",
          disabled: !validated,
        }
      ]}
    >
      <Grid>
        <Grid.Col sm={12} md={6}>
          <TextInput
            label="Name"
            placeholder="Name"
            required={validationRules?.rules.name.includes("required")}
            value={formData.name}
            onChange={(e) => {
              setFormData({
                ...formData,
                name: e.currentTarget.value,
              });
            }}
          />
        </Grid.Col>
        <Grid.Col sm={12} md={6}>
          <TextInput
            label="Email"
            placeholder="Email"
            required={validationRules?.rules.email.includes("required")}
            value={formData.email}
            onChange={(e) => {
              setFormData({
                ...formData,
                email: e.currentTarget.value,
              });
            }}
          />
        </Grid.Col>
        <Grid.Col sm={12} md={6}>
          <Select
            label="Role"
            placeholder="Role"
            required={validationRules?.rules.role_id.includes("required")}
            data={
              dropdowns.roles.map((role) => ({
                label: role.name,
                value: role.id?.toString() || "",
              }))
            }
            searchable
            onChange={(value) => {
              setFormData({
                ...formData,
                role_id: Number(value),
              });
            }}
            nothingFound="No roles found"
          />
        </Grid.Col>
      </Grid>
    </CreateView>
  );
}
export default CreateUser;
